import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy,
  Share2,
  Gift,
  Users,
  Coins,
  Diamond,
  Facebook,
  MessageCircle,
} from "lucide-react";

export default function Invite() {
  const { user } = useAuth();
  const [inviteData, setInviteData] = useState(null);

  useEffect(() => {
    api.get("/invite")
      .then(res => setInviteData(res.data))
      .catch(() => toast.error("Failed to load invite info"));
  }, []);

  // Copy only the invite code
  const copyCode = () => {
    navigator.clipboard.writeText(inviteData.invite_code)
      .then(() => toast.success("Invite code copied!"))
      .catch(() => toast.error("Copy failed"));
  };

  // Copy the invite link
  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(inviteData.invite_url)
      .then(() => toast.success("Invite link copied!"))
      .catch(() => toast.error("Copy failed"));
  };

  // Copy the pre‑written message (code + link) – exactly what is shown in the textarea
  const copyMessageToClipboard = () => {
    const message = `💜 Join Haven – a safe dating community for HIV+ individuals.\n\nUse my invite code: ${inviteData.invite_code}\nOr use the link: ${inviteData.invite_url}\n\nYou'll get 50 free tokens!`;
    navigator.clipboard.writeText(message)
      .then(() => toast.success("Invite message copied! Share it anywhere."))
      .catch(() => toast.error("Copy failed"));
  };

  // Native share (mobile)
  const shareNow = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Haven – Safe Dating Community",
          text: `Use my invite code: ${inviteData.invite_code}\nOr link: ${inviteData.invite_url}\nYou'll get 50 free tokens!`,
          url: inviteData.invite_url,
        });
        toast.success("Shared!");
      } catch (err) {
        if (err.name !== "AbortError") copyLinkToClipboard();
      }
    } else {
      copyLinkToClipboard();
    }
  };

  // Share to Facebook / WhatsApp
  const shareOn = (platform) => {
    const text = encodeURIComponent(
      `Use my invite code: ${inviteData.invite_code}\nOr link: ${inviteData.invite_url}\nYou'll get 50 free tokens!`
    );
    let url = "";
    if (platform === "facebook") {
      url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(inviteData.invite_url)}&quote=${text}`;
    } else if (platform === "whatsapp") {
      url = `https://wa.me/?text=${text}`;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!inviteData) return <div className="min-h-screen"><Navbar /><div className="p-8 text-center">Loading...</div></div>;

  // The full invite message that will be displayed and copied
  const inviteMessage = `💜 Join Haven – a safe dating community for HIV+ individuals.\n\nUse my invite code: ${inviteData.invite_code}\nOr use the link: ${inviteData.invite_url}\n\nYou'll get 50 free tokens!`;

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <Gift className="w-16 h-16 text-purple-600 mx-auto mb-4" />
          <h1 className="font-display text-3xl font-bold text-[#0F0F10]">Invite Friends & Earn</h1>
          <p className="text-gray-500 mt-2">Share your invite code and earn tokens & diamonds when friends join.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white border rounded-2xl p-5 text-center">
            <Users className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{inviteData.invited_count}</p>
            <p className="text-sm text-gray-500">Friends Invited</p>
          </div>
          <div className="bg-white border rounded-2xl p-5 text-center">
            <Coins className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{inviteData.tokens_earned}</p>
            <p className="text-sm text-gray-500">Tokens Earned</p>
          </div>
          <div className="bg-white border rounded-2xl p-5 text-center">
            <Diamond className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{inviteData.diamonds_earned}</p>
            <p className="text-sm text-gray-500">Diamonds Earned</p>
          </div>
          <div className="bg-white border rounded-2xl p-5 text-center">
            <Gift className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-sm font-semibold">+5 💎</p>
            <p className="text-xs text-gray-500">When they buy Premium</p>
          </div>
        </div>

        {/* Invite Code & Link */}
        <div className="bg-white border rounded-2xl p-6 mb-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase text-gray-500 mb-2 block">Your Invite Code</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inviteData.invite_code}
                readOnly
                className="neo-input flex-1 text-center text-xl font-mono tracking-widest cursor-text select-all"
              />
              <button onClick={copyCode} className="neo-btn bg-purple-600 border-purple-600 text-white">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-gray-500 mb-2 block">Your Invite Link</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={inviteData.invite_url}
                readOnly
                className="neo-input flex-1 cursor-text select-all"
              />
              <button onClick={copyLinkToClipboard} className="neo-btn bg-purple-600 border-purple-600 text-white">
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={shareNow} className="neo-btn bg-green-600 border-green-600 text-white flex-1">
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button onClick={() => shareOn("whatsapp")} className="neo-btn bg-[#25D366] border-[#25D366] text-white">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </button>
            <button onClick={() => shareOn("facebook")} className="neo-btn bg-[#1877F2] border-[#1877F2] text-white">
              <Facebook className="w-4 h-4" /> FB
            </button>
          </div>

          {/* Invite Message Preview + Copy Button */}
          <div className="space-y-2">
            <label className="text-xs font-semibold uppercase text-gray-500 block">Invite Message</label>
            <textarea
              readOnly
              value={inviteMessage}
              rows={5}
              className="neo-input resize-none text-sm"
            />
            <button
              onClick={copyMessageToClipboard}
              className="w-full neo-btn bg-orange-500 border-orange-500 text-white flex items-center justify-center gap-2"
            >
              <Copy className="w-4 h-4" /> Copy Invite Message
            </button>
            <p className="text-xs text-gray-400 text-center">
              This message will be copied to your clipboard.
            </p>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-purple-50 border border-purple-100 rounded-2xl p-5 text-sm text-purple-900">
          <h3 className="font-semibold mb-2">How it works</h3>
          <ul className="space-y-1.5 list-disc list-inside">
            <li>Share your invite code or link with friends.</li>
            <li>When they sign up with your code, you get <strong>50 tokens + 1 diamond</strong>.</li>
            <li>They also get <strong>50 free tokens</strong>!</li>
            <li>If they later purchase Premium, you get an extra <strong>5 diamonds</strong>.</li>
          </ul>
        </div>
      </main>
    </div>
  );
}